import {SchemaInterfaceWithFields} from 'procedures/design/utils/equipmentSchemaHelpers';
import {useNullableProcedurePageContext} from 'procedures/ProcedurePageContext';
import {extractMethodNodesFromStaticFlowchart} from 'procedures/processes/MethodNodeUtils';
import {
  useAllTagSchemasWithProceduresShape,
  useAllTagSchemasWithStandardShape,
} from 'procedures/utils/useAllTagSchemas';
import useArchivedLibraryParameterDefinitionIds, {
  getLibraryParameterDefinitionIdsFromMethods,
} from 'procedures/utils/useArchivedLibraryParameterDefinitionIds';
import useBioentitiesForProcedures, {
  getBioentityIdsFromMethods,
  BioentityForProcedure,
} from 'procedures/utils/useBioentitiesForProcedures';
import useSchemaInterfacesWithFields from 'procedures/utils/useSchemaInterfacesWithFields';
import {ApplicationFlowchartValidationContextResult} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/ApplicationFlowchartValidationContextResult';
import {StaticTemplateFlowchart} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

/**
 * Generates a stable key for Procedures validation context based on loaded data.
 * This allows detection of when new data is loaded and validation should re-run.
 */
function getProcedureValidationContextKey(
  bioentitiesForProcedures: Array<BioentityForProcedure> | null,
  archivedLibraryParameterDefinitionIds: Set<string> | null,
  schemaInterfaces: Array<SchemaInterfaceWithFields> | null
): string {
  if (bioentitiesForProcedures == null || archivedLibraryParameterDefinitionIds == null) {
    return 'unloaded';
  }
  const bioentityKey = bioentitiesForProcedures
    .map((b) => b.id)
    .sort((a, b) => a.localeCompare(b))
    .join(',');
  const archivedLibDefKey = [...archivedLibraryParameterDefinitionIds]
    .sort((a, b) => a.localeCompare(b))
    .join(',');
  // Fieldsets are not required for validation to run, so re-run validation once they load
  const schemaInterfacesKey = schemaInterfaces == null ? 'fieldsets-unloaded' : 'fieldsets-loaded';
  return `${bioentityKey}|${archivedLibDefKey}|${schemaInterfacesKey}`;
}

/**
 * Procedure-specific hook to compute validation context for PoW procedure flowchart validation.
 *
 * This hook is introduced to be used for PoW procedures only.
 * It provides the validation context needed for procedure flowcharts, including:
 * - Tag schemas (both procedures and standard shapes)
 * - Bioentities referenced in the flowchart
 * - Procedure type
 *
 * This hook can be safely called even when not in a procedure context - it will return an empty object
 * if the procedure page context is not available.
 *
 * @param draftFlowchartConfig The draft flowchart configuration
 * @returns Validation context object with context data, isLoaded flag (true when all data is non-null),
 *          and contextKey for tracking entity changes
 */
export function useNullableProcedureFlowchartValidationContext(
  draftFlowchartConfig: StaticTemplateFlowchart
): ApplicationFlowchartValidationContextResult {
  // All hooks must be called unconditionally to comply with Rules of Hooks
  const procedurePageContext = useNullableProcedurePageContext();
  const tagSchemasForProcedures = useAllTagSchemasWithProceduresShape();
  const tagSchemas = useAllTagSchemasWithStandardShape();
  const methodNodes = extractMethodNodesFromStaticFlowchart(draftFlowchartConfig);
  const currentBioentityIds = getBioentityIdsFromMethods(methodNodes);
  const bioentitiesForProcedures = useBioentitiesForProcedures(currentBioentityIds);
  const currentLibraryParameterDefinitionIds = getLibraryParameterDefinitionIdsFromMethods(methodNodes);
  const archivedLibraryParameterDefinitionIds = useArchivedLibraryParameterDefinitionIds(
    currentLibraryParameterDefinitionIds
  );
  const schemaInterfaces = useSchemaInterfacesWithFields();

  // If not in a procedure context, return empty object with loaded state
  if (procedurePageContext == null) {
    return {
      context: {},
      isLoaded: true,
      contextKey: 'default',
    };
  }

  const {procedureMetadata} = procedurePageContext;

  // Check if all data has been loaded (non-null). This is needed to let validation hooks know that the context
  // is ready to be used. If any of the required data is still null, we consider the context not loaded.
  const isLoaded =
    tagSchemasForProcedures != null &&
    tagSchemas != null &&
    bioentitiesForProcedures != null &&
    archivedLibraryParameterDefinitionIds != null;

  const context = {
    tagSchemasForProcedures,
    tagSchemas,
    bioentitiesForProcedures,
    archivedLibraryParameterDefinitionIds,
    schemaInterfaces,
    procedureType: procedureMetadata.procedureType,
    isPoWProcedure: true,
  };

  return {
    context,
    isLoaded,
    contextKey: getProcedureValidationContextKey(
      bioentitiesForProcedures,
      archivedLibraryParameterDefinitionIds,
      schemaInterfaces
    ),
  };
}
